import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/context/authStore';
import { useEnsureTenant } from '@/hooks/useTenant';
import { useSyncCurrentUser } from '@/hooks/useCurrentUser';
import Logo from '@/components/ui/Logo';
import ChangePasswordModal from '@/components/ChangePasswordModal';
import ImpersonationBanner from '@/components/ImpersonationBanner';
import InstallPWAButton from '@/components/InstallPWAButton';
import { ModulePermission } from '@/types';
import {
  Home,
  Package,
  Users,
  UsersRound,
  PawPrint,
  ShoppingCart,
  Bell,
  LogOut,
  Menu,
  X,
  Tag,
  Key,
  Building2,
  Scissors,
  Stethoscope,
  ClipboardList,
  Warehouse,
  Split,
  Banknote,
  Truck,
  FileText,
  BarChart3,
  Wallet,
  MessageCircle,
  CreditCard,
  LucideIcon,
} from 'lucide-react';
import { useState, useMemo } from 'react';
import clsx from 'clsx';

// Navegación con permisos requeridos
interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
  permission?: ModulePermission; // Si no tiene permission, siempre se muestra
  anyPermission?: ModulePermission[]; // Visible si tiene AL MENOS UNO de estos
  requireFlag?: 'has_veterinary' | 'has_laboratory' | 'has_billing_arca' | 'has_fraccionados';
}

// Navegación principal
const allNavigation: NavItem[] = [
  { name: 'Dashboard', href: '/dashboard', icon: Home, permission: 'dashboard' },
  { name: 'Pacientes', href: '/dashboard/patients', icon: PawPrint, permission: 'patients' },
  { name: 'Clientes', href: '/dashboard/owners', icon: Users, permission: 'owners' },
  { name: 'Productos', href: '/dashboard/products', icon: Package, permission: 'products' },
  { name: 'Proveedores', href: '/dashboard/suppliers', icon: Truck, permission: 'purchases' },
  { name: 'Compras', href: '/dashboard/purchase-orders', icon: FileText, permission: 'purchases' },
  { name: 'Inventario', href: '/dashboard/inventory', icon: Warehouse, anyPermission: ['inventory', 'products_write'] },
  { name: 'Fraccionados', href: '/dashboard/fraccionados', icon: Split, permission: 'products_write', requireFlag: 'has_fraccionados' },
  { name: 'Categorías', href: '/dashboard/categories', icon: Tag, permission: 'products_write' },
  { name: 'Peluquería', href: '/dashboard/grooming', icon: Scissors, permission: 'grooming' },
  { name: 'Turnos Médicos', href: '/dashboard/medical-appointments', icon: Stethoscope, permission: 'medical_appointments' },
  { name: 'Comandas', href: '/dashboard/service-orders', icon: ClipboardList, permission: 'service_orders' },
  { name: 'Ventas', href: '/dashboard/sales', icon: ShoppingCart, permission: 'sales' },
  { name: 'Presupuestos', href: '/dashboard/budgets', icon: FileText, permission: 'sales' },
  { name: 'Caja', href: '/dashboard/cash-register', icon: Banknote, permission: 'cash_register' },
  { name: 'Finanzas', href: '/dashboard/finances', icon: Wallet, permission: 'finances' },
  { name: 'Reportes', href: '/dashboard/reports', icon: BarChart3, permission: 'dashboard' },
  { name: 'Alertas', href: '/dashboard/alerts', icon: Bell, permission: 'alerts' },
];

// Navegación de configuración
const configNavigation: NavItem[] = [
  { name: 'Usuarios', href: '/dashboard/users', icon: UsersRound, permission: 'users' },
  { name: 'Mi Veterinaria', href: '/dashboard/vet-profile', icon: Building2, permission: 'users' },
  { name: 'Recordatorios', href: '/dashboard/reminders', icon: MessageCircle, permission: 'reminders' },
  { name: 'Facturación', href: '/dashboard/billing', icon: FileText, permission: 'users', requireFlag: 'has_billing_arca' },
  { name: 'Mi Suscripción', href: '/dashboard/my-subscription', icon: CreditCard, permission: 'users' },
];


export default function Layout() {
  useEnsureTenant(); // Red de seguridad: tenant siempre del usuario logueado
  useSyncCurrentUser(); // Permisos siempre frescos (si el admin los cambia, no hay que re-loguearse)
  const { user, tenant, logout, impersonation } = useAuthStore();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  // El cambio de contraseña obligatorio ahora se maneja en /change-password
  // Este modal solo se usa para cambio voluntario de contraseña

  // Obtener permisos efectivos del usuario
  const userPermissions = useMemo(() => {
    return user?.effective_permissions || [];
  }, [user?.effective_permissions]);

  // Verificar si el usuario tiene permiso para un módulo
  const hasPermission = (permission?: ModulePermission): boolean => {
    if (!permission) return true; // Sin permiso requerido = siempre visible
    return userPermissions.includes(permission);
  };

  // Visible si cumple el permiso simple Y (si tiene anyPermission) al menos uno de esos
  const canSeeNav = (item: NavItem): boolean => {
    if (item.anyPermission && item.anyPermission.length > 0) {
      return item.anyPermission.some((p) => userPermissions.includes(p));
    }
    return hasPermission(item.permission);
  };

  const hasFlag = (flag?: 'has_veterinary' | 'has_laboratory' | 'has_billing_arca' | 'has_fraccionados') => {
    if (!flag) return true;
    if (!tenant) return false;
    return Boolean((tenant as any)[flag]);
  };

  // Filtrar navegación principal según permisos y flags del tenant
  const filteredNavigation = useMemo(() => {
    return allNavigation.filter(item => canSeeNav(item) && hasFlag(item.requireFlag));
  }, [userPermissions, tenant]);

  // Filtrar navegación de configuración según permisos y flags del tenant
  const filteredConfigNavigation = useMemo(() => {
    return configNavigation.filter(item => hasPermission(item.permission) && hasFlag(item.requireFlag));
  }, [userPermissions, tenant]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Impersonation Banner */}
      <ImpersonationBanner />

      <div className={`flex flex-1 ${impersonation.isImpersonating ? 'pt-12' : ''}`}>
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={clsx(
          'fixed inset-y-0 left-0 z-30 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:relative',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b safe-top">
            <Link to="/dashboard" className="flex items-center gap-2">
              <Logo
                src={tenant?.logo_url}
                size="sm"
                showText
                text={tenant?.name || 'ANIMAvet'}
              />
            </Link>
            <button
              className="lg:hidden p-2 rounded-md hover:bg-gray-100"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
            {filteredNavigation.map((item) => {
              const isActive = location.pathname === item.href;
              const Icon = item.icon;

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={clsx(
                    'flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  )}
                >
                  <Icon className="h-5 w-5 mr-3" />
                  {item.name}
                </Link>
              );
            })}

            {/* Navegación de Configuración (si tiene algún permiso de config) */}
            {filteredConfigNavigation.length > 0 && (
              <div className="pt-4 mt-4 border-t">
                <p className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                  Configuración
                </p>
                {filteredConfigNavigation.map((item) => {
                  const isActive = location.pathname === item.href;
                  const Icon = item.icon;

                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setSidebarOpen(false)}
                      className={clsx(
                        'flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-gray-700 hover:bg-gray-100'
                      )}
                    >
                      <Icon className="h-5 w-5 mr-3" />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            )}

            {/* Link al Panel de Admin para SUPERADMIN */}
            {user?.role === 'superadmin' && (
              <div className="pt-4 mt-4 border-t">
                <p className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                  Administración
                </p>
                <Link
                  to="/admin"
                  onClick={() => setSidebarOpen(false)}
                  className="flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors text-indigo-600 hover:bg-indigo-50"
                >
                  <Building2 className="h-5 w-5 mr-3" />
                  Panel de Admin
                </Link>
              </div>
            )}
          </nav>

          {/* Botón instalar como app (PWA) */}
          <div className="px-2 pb-2">
            <InstallPWAButton />
          </div>

          {/* User section */}
          <div className="border-t p-4 safe-bottom">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                  <span className="text-primary-700 font-medium">
                    {user?.full_name.charAt(0).toUpperCase()}
                  </span>
                </div>
              </div>
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.full_name}
                </p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              </div>
              <button
                onClick={() => setShowPasswordModal(true)}
                className="p-2 rounded-md hover:bg-gray-100 text-gray-500"
                title="Cambiar contraseña"
              >
                <Key className="h-5 w-5" />
              </button>
              <button
                onClick={logout}
                className="p-2 rounded-md hover:bg-gray-100 text-gray-500"
                title="Cerrar sesión"
              >
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de cambio de contraseña (voluntario) */}
      <ChangePasswordModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
      />

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen overflow-auto">
        {/* Top bar (mobile) */}
        <div className="lg:hidden sticky top-0 z-10 bg-white shadow-sm safe-top">
          <div className="flex items-center justify-between h-16 px-4">
            <button
              className="p-2 rounded-md hover:bg-gray-100"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-6 w-6" />
            </button>
            <Logo
              src={tenant?.logo_url}
              size="sm"
              showText
              text={tenant?.name || 'ANIMAvet'}
            />
            <div className="w-10" /> {/* Spacer */}
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
      </div>
    </div>
  );
}
